import { UserService } from "@/services";
import { Localforage } from "@/boot/localforage/initLocalforage";
import { MapState } from "@/helpers/MapState";
import { Appearance } from "@/models/UserPreference";

const userService = new UserService();
const LOGOUT_URL = env.APP_URL + "/api/oauth/token/revoke";

export const initAuthStore = vueAuth => ({
  state: {
    isAuthenticated: false,
    defaultRoute: '/welcome',
    hasFrontendAccess: false,
    hasApiRole: false,
  },
  mutations: {
    isAuthenticated(state, payload) {
      state.isAuthenticated = payload.isAuthenticated;
    },
    setJwt(state, { access_token, expires_in, refresh_token, token_type }) {
      const now = new Date();

      state.jwt = {
        access_token: access_token,
        expires_in: expires_in * 1000,
        expires_on: new Date(now.getTime() + expires_in * 1000).getTime(),
        refresh_token: refresh_token,
        token_type: token_type
      };
    },
    clearJwt(state) {
      state.jwt = null;
    },
    setDefaultRoute(state, route){
      state.defaultRoute = route;
    },
  },
  actions: {
    login(context, payload) {
      const data = {
        ...payload.user
        // client_id: env.client_id,
        // client_secret: env.client_secret,
        // grant_type: "password"
      };

      return vueAuth.login(data, payload.requestOptions).then(({ data }) => {
        Localforage.enableWrites();
        context.commit("setJwt", data);

        context.commit("isAuthenticated", {
          isAuthenticated: vueAuth.isAuthenticated()
        });
      });
    },
    forgotPassword(context, payload) {
      return userService.forgotPassword(payload);
    },
    resetPassword(contect, payload) {
      return userService.resetPassword(payload);
    },
    logoutOffline(context) {
      context.commit("isAuthenticated", {
        isAuthenticated: false
      });

      return context.dispatch("logout", { url: null });
    },
    logout(context, payload = null) {
      if (!payload) payload = { url: LOGOUT_URL };

      context.commit("isAuthenticated", {
        isAuthenticated: false
      });

      return Localforage.clearAll()
        .catch(() => null)
        .then(() => {
          MapState.clear();
          return vueAuth.logout(payload);
        })
        .catch(() => null)
        .then(() => {
          context.commit("clearJwt");
          context.commit("clearUser");
          context.commit("setDefaultRoute", "/welcome");

          return context.commit("isAuthenticated", {
            isAuthenticated: false
          });
        });
    },
    fetchUser(context) {
      return userService.fetchUser().then(user => {

        context.commit('setDefaultRoute', user.has_frontend_access ? '/' : '/account');

        // A user without a user_preferences row, or one with a null appearance, used
        // to throw here - and because this rejection leaves isAuthenticated set with
        // no user behind it, the router guard then had nothing to decide on and
        // redirected in a loop until the tab died. One such user exists in
        // production. The same fallback the appearance getter in initStore already
        // applies.
        context.dispatch(
          "setAppearance",
          (user.preference?.appearance ?? Appearance.DEFAULT).toLowerCase()
        );

        context.dispatch("setUser", user);

        return user;
      });
    },
    refreshAccessToken(context) {
      return vueAuth
        .login({
          refresh_token: context.getters.refreshToken,
        })
        .then(({ data }) => {
          context.commit("setJwt", data);

          context.commit("isAuthenticated", {
            isAuthenticated: vueAuth.isAuthenticated()
          });
        });
    }
  },
  getters: {
    accessToken(state) {
      return state?.jwt?.access_token;
    },
    refreshToken(state) {
      return state?.jwt?.refresh_token;
    },
    accessTokenExpirationDate(state) {
      return state?.jwt?.expires_on ? new Date(state.jwt.expires_on) : null;
    },
    shouldRefreshToken(state, store) {
      const now = new Date();
      const tolerance = 14 * 24 * 60 * 60 * 1000; // 14 days

      if (!store.accessToken || !store.accessTokenExpirationDate) return false;

      return (
        store.accessTokenExpirationDate.getTime() < now.getTime() + tolerance
      );
    },
    isAuthenticated(state) {
      return state.isAuthenticated;
    },
    defaultRoute(state) {
      return state.defaultRoute;
    },
  }
});
